import { configureStore, combineReducers } from '@reduxjs/toolkit'
import { useDispatch, useSelector } from 'react-redux'
import { persistStore, persistReducer } from 'redux-persist'
import storage from 'redux-persist/es/storage'

import authReducer from './authSlice'
import ticketReducer from './ticketSlice'
import uiReducer from './uiSlice'

const rootReducer = combineReducers({
	auth: authReducer,
	ticket: ticketReducer,
	ui: uiReducer,
})

// redux-persist with localStorage
// This stores JWT in localStorage for cross-session persistence (page refresh, browser close)
// Security note: localStorage is readable via XSS — this is a known trade-off
// Plan: Replace with httpOnly cookies in follow-up story when backend supports it
const persistConfig = {
	key: 'root',
	storage,
	whitelist: ['auth'], // Only persist auth slice
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [
					'persist/PERSIST',
					'persist/REHYDRATE',
					'persist/FLUSH',
					'persist/PAUSE',
					'persist/PURGE',
					'persist/REGISTER',
				],
			},
		}),
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

// Typed hooks — use these throughout the app instead of plain useDispatch/useSelector
export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()

export const persistor = persistStore(store)
